import { useAuth } from '@/hooks/useAuth';
import { useActiveTenant, useTenants } from '@/hooks/useActiveTenant';
import { Search, Bell, ChevronDown, Check } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

export function Topbar({ breadcrumb }: { breadcrumb?: React.ReactNode }) {
  const { user } = useAuth();
  const { tenantId, setTenantId } = useActiveTenant();
  const { data: tenants } = useTenants();
  const [open, setOpen] = useState(false);
  const ddRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ddRef.current && !ddRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const isSuperadmin = user?.is_superadmin;
  const activeTenant = (tenants ?? []).find((t) => t.id === tenantId);
  const currentLabel = activeTenant?.name ?? (isSuperadmin ? 'Tous les tenants' : user?.tenant?.name ?? '—');

  return (
    <header className="h-12 border-b border-border bg-bg-2 flex items-center px-4 gap-3">
      {/* Tenant switcher (superadmin) */}
      <div className="relative" ref={ddRef}>
        <button
          onClick={() => isSuperadmin && setOpen((o) => !o)}
          className={`flex items-center gap-2 px-2 h-7 rounded text-sm ${isSuperadmin ? 'hover:bg-surface-hi cursor-pointer' : 'cursor-default'}`}>
          <span className={`w-3 h-3 rounded-full ${activeTenant ? 'bg-brand' : 'bg-text-faint/40'}`} />
          <span className="font-medium">{currentLabel}</span>
          {isSuperadmin && <ChevronDown size={12} className="text-text-faint" />}
        </button>
        {open && isSuperadmin && (
          <div className="absolute top-full left-0 mt-1 bg-bg-2 border border-border rounded shadow-lg min-w-48 z-50 py-1">
            <button
              onClick={() => { setTenantId(null); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-surface-hi text-left">
              <span className="w-4">{tenantId === null && <Check size={12} />}</span>
              <span className="italic text-text-muted">Tous les tenants</span>
            </button>
            <div className="border-t border-border my-1" />
            {(tenants ?? []).map((t) => (
              <button key={t.id}
                onClick={() => { setTenantId(t.id); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-surface-hi text-left">
                <span className="w-4">{tenantId === t.id && <Check size={12} />}</span>
                <span className="w-2 h-2 rounded-full bg-brand" />
                <span className="font-medium">{t.name}</span>
                <span className="text-text-faint text-[10.5px] ml-auto">{t.slug}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="text-text-faint">/</div>
      <div className="flex-1 text-sm text-text-muted">{breadcrumb}</div>

      <button className="btn btn-ghost btn-sm gap-2 px-3">
        <Search size={12} />
        <span>Rechercher</span>
        <kbd className="ml-2 px-1.5 py-0.5 text-[10px] bg-bg border border-border rounded font-mono">⌘K</kbd>
      </button>

      <button className="btn btn-ghost btn-sm">
        <Bell size={13} />
      </button>

      <div className="flex items-center gap-2 pl-2 border-l border-border">
        <div className="w-7 h-7 rounded-full bg-brand-dim flex items-center justify-center text-brand text-xs font-semibold">
          {user?.email?.[0]?.toUpperCase() ?? '?'}
        </div>
        <div className="text-xs leading-tight">
          <div className="text-text">{user?.name ?? user?.email}</div>
          <div className="text-text-faint">{user?.role}</div>
        </div>
      </div>
    </header>
  );
}
